import React, { useCallback, useEffect, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import { useCsAgentRoster } from '~/hooks/apis/reporting/useCsAgentRoster';
import { useCsDashboardStats } from '~/hooks/apis/reporting/useCsDashboardStats';
import useUrlQuery from '~/hooks/useQuery';

import CsDashboardAgentTable from './CsDashboardAgentTable';
import CsDashboardChart from './CsDashboardChart';
import CsDashboardFilters from './CsDashboardFilters';
import {
  buildChartDataForFilteredTeam,
  buildChartDataFromAgentRows,
  buildCsAgentRosterRows,
  buildMergedAgentRows,
  computeMaxCellCount,
  getAllDates,
  onlyCompletedSydneyChartData,
  onlyCompletedSydneyDates,
} from './csDashboardUtils';
import { CS_FEATURE_MAP, DEFAULT_CS_FEATURE_IDS, MAX_SELECTED_CS_FEATURES } from './features/registry';

export default function CsDashboardContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlQuery = useUrlQuery();

  const selectedTeamLeader = urlQuery.get('teamLeader') || '';
  const selectedDepartment = urlQuery.get('department') || '';
  const selectedSubDepartment = urlQuery.get('subDepartment') || '';
  const startAt = urlQuery.get('start_at') || '';
  const endAt = urlQuery.get('end_at') || '';

  const rawFeatures = urlQuery.get('features');
  const parsedFeatureIds: string[] = rawFeatures
    ? rawFeatures
        .split(',')
        .filter((id) => CS_FEATURE_MAP[id])
        .slice(0, MAX_SELECTED_CS_FEATURES)
    : [];
  const selectedFeatureIds = parsedFeatureIds.length > 0 ? parsedFeatureIds : DEFAULT_CS_FEATURE_IDS;

  const updateUrlParams = useCallback(
    (updates: Record<string, string>) => {
      const params = new URLSearchParams(location.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  const { data, isLoading, error } = useCsDashboardStats({
    featureIds: selectedFeatureIds,
    startAt: startAt || undefined,
    endAt: endAt || undefined,
  });

  const { data: rosterData, isLoading: isRosterLoading, error: rosterError } = useCsAgentRoster();
  const roster = rosterData?.result.agents;
  const teamLeaders = rosterData?.result.teamLeaders ?? [];
  const departments = rosterData?.result.departments ?? [];

  const rosterRows = useMemo(() => buildCsAgentRosterRows(roster ?? []), [roster]);
  const subDepartments = useMemo(() => {
    if (!selectedDepartment) return [];

    return Array.from(
      new Set(
        rosterRows
          .filter((row) => row.department === selectedDepartment && row.subDepartment)
          .map((row) => row.subDepartment as string),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [rosterRows, selectedDepartment]);

  useEffect(() => {
    if (!selectedDepartment && selectedSubDepartment) {
      updateUrlParams({ subDepartment: '' });
      return;
    }
    if (!selectedDepartment || !selectedSubDepartment || !roster) return;
    if (subDepartments.includes(selectedSubDepartment)) return;

    updateUrlParams({ subDepartment: '' });
  }, [roster, selectedDepartment, selectedSubDepartment, subDepartments, updateUrlParams]);

  const allAgentRows = useMemo(
    // Only merge against the roster once it has loaded
    () => buildMergedAgentRows(data?.result.features, roster ? rosterRows : undefined),
    [data, roster, rosterRows],
  );

  const hasRosterFilters = Boolean(selectedTeamLeader || selectedDepartment || selectedSubDepartment);
  const filteredAgentEmails = useMemo<Set<string> | null>(() => {
    if (!hasRosterFilters) return null;
    const emails = new Set<string>();
    allAgentRows.forEach((row) => {
      const matchesTeamLeader = !selectedTeamLeader || row.teamLeader === selectedTeamLeader;
      const matchesDepartment = !selectedDepartment || row.department === selectedDepartment;
      const matchesSubDepartment = !selectedSubDepartment || row.subDepartment === selectedSubDepartment;

      if (matchesTeamLeader && matchesDepartment && matchesSubDepartment) {
        emails.add(row.agentEmail);
      }
    });
    return emails;
  }, [allAgentRows, hasRosterFilters, selectedDepartment, selectedSubDepartment, selectedTeamLeader]);

  const visibleRows = useMemo(() => {
    if (!filteredAgentEmails) return allAgentRows;
    return allAgentRows.filter((row) => filteredAgentEmails.has(row.agentEmail));
  }, [allAgentRows, filteredAgentEmails]);

  const chartData = useMemo(() => {
    const unfilteredChartData = filteredAgentEmails
      ? buildChartDataForFilteredTeam(selectedFeatureIds, allAgentRows, filteredAgentEmails)
      : buildChartDataFromAgentRows(selectedFeatureIds, allAgentRows);

    return onlyCompletedSydneyChartData(unfilteredChartData);
  }, [filteredAgentEmails, selectedFeatureIds, allAgentRows]);

  const allDates = useMemo(
    () => onlyCompletedSydneyDates(getAllDates(allAgentRows, selectedFeatureIds)),
    [allAgentRows, selectedFeatureIds],
  );

  const maxCellCount = useMemo(
    () => computeMaxCellCount(visibleRows, selectedFeatureIds, allDates),
    [visibleRows, selectedFeatureIds, allDates],
  );

  const chartTitle = `Daily feature usage — total across ${hasRosterFilters ? 'selected agents' : 'all agents'}`;
  const isDashboardLoading = isLoading || (hasRosterFilters && isRosterLoading);
  const dashboardError = error || (hasRosterFilters ? rosterError : null);

  return (
    <>
      <Box mt={4}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body1">
            Tracks CS agent adoption of new features. Data is sourced from Snowplow events via BigQuery and refreshed
            periodically by the analytics pipeline — expect a few hours of latency between an event firing and it
            appearing here.
          </Typography>
        </Alert>
      </Box>

      <CsDashboardFilters
        selectedFeatureIds={selectedFeatureIds}
        selectedTeamLeader={selectedTeamLeader}
        selectedDepartment={selectedDepartment}
        selectedSubDepartment={selectedSubDepartment}
        startAt={startAt}
        endAt={endAt}
        teamLeaders={teamLeaders}
        departments={departments}
        subDepartments={subDepartments}
        updateUrlParams={updateUrlParams}
      />

      {error && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading dashboard stats: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {rosterError && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading CS agent roster: {rosterError instanceof Error ? rosterError.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isDashboardLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isDashboardLoading && !dashboardError && data && visibleRows.length > 0 && (
        <CsDashboardChart chartData={chartData} selectedFeatureIds={selectedFeatureIds} title={chartTitle} />
      )}

      {!isDashboardLoading && !dashboardError && data && visibleRows.length > 0 && (
        <CsDashboardAgentTable
          visibleRows={visibleRows}
          selectedFeatureIds={selectedFeatureIds}
          allDates={allDates}
          maxCellCount={maxCellCount}
        />
      )}

      {!isDashboardLoading && !dashboardError && data && visibleRows.length === 0 && (
        <Box mt={4}>
          <Alert severity="info">
            {hasRosterFilters
              ? 'No agents match the selected filters.'
              : 'No agent activity found for the selected date range and features.'}
          </Alert>
        </Box>
      )}
    </>
  );
}
